import { useState } from 'react';
import { ActionIcon, Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

export interface ContactPersonRow {
  id: string;
  fullName: string;
  position: string | null;
  phone: string | null;
  email: string | null;
}

interface Props {
  persons: ContactPersonRow[];
  canEdit: boolean;
  onAdd: (data: { fullName: string; position?: string | null; phone?: string | null; email?: string | null }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const emptyForm = { fullName: '', position: '', phone: '', email: '' };

export function ContactPersonsEditor({ persons, canEdit, onAdd, onDelete }: Props) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleAdd() {
    setError(null);
    if (!form.fullName.trim()) return setError('Укажите ФИО');

    setBusy(true);
    try {
      await onAdd({
        fullName: form.fullName.trim(),
        position: form.position || null,
        phone: form.phone || null,
        email: form.email || null,
      });
      setForm(emptyForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить контактное лицо');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="sm">
      {persons.length > 0 && (
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ФИО</Table.Th>
              <Table.Th>Должность</Table.Th>
              <Table.Th>Контакты</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {persons.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <Text size="sm">{p.fullName}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {p.position ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {[p.phone, p.email].filter(Boolean).join(', ') || '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {canEdit && (
                    <ActionIcon variant="subtle" color="red" onClick={() => void onDelete(p.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {canEdit && (
        <Stack gap={6}>
          <Group grow>
            <TextInput
              placeholder="ФИО"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
            />
            <TextInput
              placeholder="Должность"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.currentTarget.value })}
            />
          </Group>
          <Group grow>
            <TextInput
              placeholder="Телефон"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
            />
            <TextInput
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
            />
          </Group>
          {error && (
            <Text size="xs" c="red">
              {error}
            </Text>
          )}
          <Button size="xs" variant="light" onClick={() => void handleAdd()} loading={busy} w="fit-content">
            Добавить контактное лицо
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
